/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import { ResponsiveTable } from "../../components/mobile/ResponsiveTable";
import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../../context/AppContext";
import { toast } from "../../components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Loader2, Clock, LogIn, LogOut, MapPin, FileDown, TrendingUp, CalendarPlus } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { getGeo, inp, DEPTS, useHrmsManage } from "./hrmsShared";
import HRMSLeaves from "./HRMSLeaves";
import HRMSOvertime from "./HRMSOvertime";
import { fmtD } from "../../lib/date";


export default function HRMSAttendance() {
  const { api, user, lang, featVis } = useApp();
  const zh = lang === "zh";
  const T = (z, e) => (zh ? z : e);
  const canManage = useHrmsManage(user);
  const canView = canManage;
  const [tab, setTab] = useState("mine");
  const attTabs = [["mine", true], ["report", canView], ["leaves", true], ["overtime", true]].filter(([k, d]) => featVis(`/hrms/attendance::${k}`, d)).map(([k]) => k);
  useEffect(() => { if (attTabs.length && !attTabs.includes(tab)) setTab(attTabs[0]); }, [attTabs.join(","), tab]); // eslint-disable-line
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [recs, setRecs] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState("");
  const [geoDlg, setGeoDlg] = useState(null);
  const [geoSaving, setGeoSaving] = useState(false);
  const [trend, setTrend] = useState(null);
  const [trendDept, setTrendDept] = useState("");
  // 请假快速入口（HRMS 走查一-6）：见下方「申请请假」按钮。
  // autoLeave 是一次性信号 —— 点按钮时置 true，HRMSLeaves 打开弹窗后回调把它清掉。
  // 为什么不用「切到 leaves 页签」了事：需求要的是「打卡旁边就能请假的按钮」，
  // 让用户点一下再滚到下个页签里找第二个按钮，等于没解决他反馈的那一步。
  const [autoLeave, setAutoLeave] = useState(false);

  useEffect(() => {
    if (tab === "report" && canView) {
      setTrend(null);
      api.get("/hrms/attendance/trend", { params: { weeks: 8, dept: trendDept } })
        .then(({ data }) => setTrend(data)).catch(() => setTrend([]));
    }
  }, [api, tab, canView, trendDept]);

  const openGeoDlg = async () => {
    try {
      const [{ data: s }, { data: roles }] = await Promise.all([
        api.get("/hrms/settings"), api.get("/hrms/role-catalog")]);
      setGeoDlg({ radius: s.geoRadius || 500, required: s.geoRequiredRoles || [], catalog: roles });
    } catch { toast.error(T("加载失败", "Load failed")); }
  };
  const saveGeo = async () => {
    setGeoSaving(true);
    try {
      await api.put("/hrms/settings", { geoRadius: Number(geoDlg.radius) || 500, geoRequiredRoles: geoDlg.required });
      toast.success(T("考勤定位规则已保存", "Geo rules saved")); setGeoDlg(null);
    } catch (e) { toast.error(e?.response?.data?.detail || T("保存失败", "Save failed")); }
    setGeoSaving(false);
  };
  const exportXlsx = async () => {
    try {
      const res = await api.get("/hrms/attendance/export", { params: { month }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = zh ? `考勤月报_${month}.xlsx` : `attendance_${month}.xlsx`; a.click();
      URL.revokeObjectURL(a.href);
      toast.success(T(`${month} 考勤月报已导出（含工地定位明细）`, `${month} attendance report exported (with site geo detail)`));
    } catch { toast.error(T("导出失败", "Export failed")); }
  };

  const reload = useCallback(() => {
    if (tab === "mine") api.get("/hrms/attendance", { params: { month } }).then(({ data }) => setRecs(data)).catch(() => setRecs([]));
    else if (tab === "report") api.get("/hrms/attendance/report", { params: { month } }).then(({ data }) => setReport(data)).catch(() => setReport([]));
  }, [api, tab, month]);
  useEffect(() => { reload(); }, [reload]);

  const clock = async (action) => {
    setBusy(action);
    try {
      const now = new Date();
      const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const localTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      const geo = await getGeo();
      const { data } = await api.post("/hrms/attendance/clock", { action, localDate, localTime, ...(geo || {}) });
      toast.success(action === "in" ? T(`上班打卡成功 ${localTime}`, `Clocked in ${localTime}`) : T(`下班打卡成功 ${localTime}`, `Clocked out ${localTime}`));
      if (data?.site?.address) toast.success(T(`📍 已匹配工地：${data.site.address}`, `📍 Site matched: ${data.site.address}`));
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || T("打卡失败", "Clock failed")); }
    setBusy("");
  };

  return (
    <div className="space-y-4" data-testid="hrms-attendance-page">
      <div>
        <h1 className="text-xl font-extrabold text-slate-800">{T("考勤排班", "Attendance & Scheduling")}</h1>
        <p className="text-xs text-slate-400 mt-0.5">{T("打卡 · 加州加班规则（日超 8 小时 1.5 倍 · 超 12 小时 2 倍）· 假勤审批", "Clocking · CA overtime rules (>8h 1.5× · >12h 2×) · leave approvals")}</p>
      </div>

      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 flex flex-wrap items-center gap-3">
        <Clock className="h-4 w-4 text-indigo-600" />
        <span className="text-sm font-bold text-indigo-700">{T("快捷打卡", "Quick Clock")}</span>
        <div className="flex-wrap md:flex-nowrap flex gap-2 ml-auto">
          {canManage && (
            <button data-testid="hrms-att-geo-btn" onClick={openGeoDlg} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 rounded-full border border-indigo-300 text-indigo-600 hover:bg-indigo-100 text-xs font-semibold px-4 py-2">
              <MapPin className="h-3.5 w-3.5" />{T("定位规则", "Geo Rules")}
            </button>
          )}
          <button data-testid="hrms-att-clock-in" disabled={!!busy} onClick={() => clock("in")} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-4 py-2 disabled:opacity-40">
            {busy === "in" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogIn className="h-3.5 w-3.5" />}{T("上班", "Clock In")}
          </button>
          <button data-testid="hrms-att-clock-out" disabled={!!busy} onClick={() => clock("out")} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 rounded-full bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold px-4 py-2 disabled:opacity-40">
            {busy === "out" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}{T("下班", "Clock Out")}
          </button>
          {/* 请假入口（HRMS 走查一-6）。
              需求原文："Attendance & Leave section please have the leave request blue button added to
              the top next to Clock In and Clock Out."
              原先蓝色「申请请假」只在「假勤申请」页签的内容区里，打卡页顶部看不到，
              用户要打个卡再请假得先切页签、再找第二个按钮。
              现在放在打卡按钮右侧，点一下就地弹出请假表单（复用 HRMSLeaves 的同一个弹窗，
              不另写一份表单，避免两处校验与字段口径漂移）。
              配色：需求点名要「blue button」，这里用品牌蓝 bg-brand-500 ——
              打卡条的「上班」是 indigo-600，「请假」用品牌蓝才能在语义上区分开，
              又不至于在同一行里冒出第三种互不相干的蓝。 */}
          {attTabs.includes("leaves") && (
            <button data-testid="hrms-att-leave-btn" onClick={() => { setTab("leaves"); setAutoLeave(true); }}
              className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-xs font-semibold px-4 py-2">
              <CalendarPlus className="h-3.5 w-3.5" />{T("请假", "Request Leave")}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {attTabs.includes("mine") && <button data-testid="hrms-att-tab-mine" onClick={() => setTab("mine")} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full px-3.5 py-1.5 font-semibold ${tab === "mine" ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-500"}`}>{canView ? T("考勤记录", "Records") : T("我的记录", "My Records")}</button>}
        {attTabs.includes("report") && <button data-testid="hrms-att-tab-report" onClick={() => setTab("report")} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full px-3.5 py-1.5 font-semibold ${tab === "report" ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-500"}`}>{T("月度汇总", "Monthly Summary")}</button>}
        {attTabs.includes("leaves") && <button data-testid="hrms-att-tab-leaves" onClick={() => setTab("leaves")} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full px-3.5 py-1.5 font-semibold ${tab === "leaves" ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-500"}`}>{canView ? T("假勤审批", "Leave Approvals") : T("假勤申请", "Leave Requests")}</button>}
        {attTabs.includes("overtime") && <button data-testid="hrms-att-tab-overtime" onClick={() => setTab("overtime")} className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 min-h-11 text-xs rounded-full px-3.5 py-1.5 font-semibold ${tab === "overtime" ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-500"}`}>{T("加班申请与打卡", "Overtime")}</button>}
        {tab !== "leaves" && (
          <div className="ml-auto flex items-center gap-2">
            {canManage && (
              <button data-testid="hrms-att-export-btn" onClick={exportXlsx}
                className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-4 py-1.5">
                <FileDown className="h-3.5 w-3.5" />{T("导出 Excel 月报", "Export Excel")}
              </button>
            )}
            <input data-testid="hrms-att-month" type="month" className="text-sm rounded-md border border-slate-200 px-2 py-1.5" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
        )}
      </div>

      {tab === "leaves" && <HRMSLeaves embedded autoOpen={autoLeave} onAutoOpened={() => setAutoLeave(false)} />}
      {tab === "overtime" && <HRMSOvertime month={month} />}

      <Dialog open={!!geoDlg} onOpenChange={(o) => !o && setGeoDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{T("考勤定位规则（地理围栏）", "Clock-in Geo Rules (Geofence)")}</DialogTitle></DialogHeader>
          {geoDlg && (
            <div className="space-y-3 text-sm" data-testid="hrms-geo-dlg">
              <label className="block text-xs text-slate-500">{T("打卡有效半径（米，距最近项目工地）", "Valid radius (meters from nearest project site)")}
                <input data-testid="hrms-geo-radius" type="number" min="50" max="5000" step="50" className={inp}
                  value={geoDlg.radius} onChange={(e) => setGeoDlg((d) => ({ ...d, radius: e.target.value }))} />
              </label>
              <div>
                <p className="text-xs text-slate-500 mb-1.5">{zh ? <>以下岗位<b className="text-red-600">必须在工地范围内</b>才能打上班卡（其余岗位仅记录定位不强制）</> : <>These roles <b className="text-red-600">must be on site</b> to clock in (others are logged but not enforced)</>}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-[38vh] overflow-y-auto">
                  {geoDlg.catalog.map((r) => (
                    <label key={r.role} className="flex items-center gap-2 text-xs text-slate-600 rounded-lg border border-slate-100 px-2.5 py-1.5 cursor-pointer">
                      <input data-testid={`hrms-geo-role-${r.role}`} type="checkbox" checked={geoDlg.required.includes(r.role)}
                        onChange={(e) => setGeoDlg((d) => ({ ...d, required: e.target.checked ? [...d.required, r.role] : d.required.filter((x) => x !== r.role) }))} />
                      {r.zh}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
                <button onClick={() => setGeoDlg(null)} className="text-xs rounded-full border border-slate-200 px-4 py-2">{T("取消", "Cancel")}</button>
                <button data-testid="hrms-geo-save" disabled={geoSaving} onClick={saveGeo}
                  className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold px-4 py-2 disabled:opacity-50">{geoSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("保存规则", "Save")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {tab === "mine" && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <ResponsiveTable className="w-full text-sm">
            <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
              <th className="px-3 py-2">{T("日期", "Date")}</th><th className="px-3 py-2">{T("员工", "Employee")}</th><th className="px-3 py-2">{T("上班", "In")}</th><th className="px-3 py-2">{T("下班", "Out")}</th>
              <th className="px-3 py-2">{T("定位", "Location")}</th><th className="px-3 py-2">{T("工时", "Hours")}</th><th className="px-3 py-2">OT 1.5×</th><th className="px-3 py-2">OT 2×</th><th className="px-3 py-2">{T("异常", "Flags")}</th>
            </tr></thead>
            <tbody>
              {recs === null ? <tr><td colSpan={9} className="text-center py-8 text-slate-300"><Loader2 className="h-4 w-4 animate-spin inline" /></td></tr> :
                recs.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-slate-300">{T("本月暂无打卡记录", "No clock records this month")}</td></tr> :
                recs.map((a) => (
                  <tr key={a.id} className="border-b border-slate-50" data-testid={`hrms-att-row-${a.id}`}>
                    <td className="px-3 py-2 text-slate-600">{fmtD(a.date)}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{a.employeeName}</td>
                    <td className="px-3 py-2">{a.clockIn || "-"}</td>
                    <td className="px-3 py-2">{a.clockOut || "-"}</td>
                    <td className="px-3 py-2 text-xs max-w-[180px]" data-testid={`hrms-att-loc-${a.id}`}>
                      {a.inSite ? <span className="text-emerald-600 inline-flex items-center gap-0.5" title={T(`${a.inSite.address}（距 ${a.inSite.distance}m）`, `${a.inSite.address} (${a.inSite.distance}m away)`)}><MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{a.inSite.address}</span></span>
                        : a.inLoc ? <span className="text-slate-400">{T("非项目现场", "Off-site")}</span>
                        : <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-3 py-2 font-semibold text-slate-700">{a.hours || "-"}</td>
                    <td className="px-3 py-2">{a.ot15 ? <span className="text-amber-600 font-semibold">{a.ot15}h</span> : "-"}</td>
                    <td className="px-3 py-2">{a.ot20 ? <span className="text-red-600 font-semibold">{a.ot20}h</span> : "-"}</td>
                    <td className="px-3 py-2 text-xs">
                      {a.late && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold mr-1">{T("迟到", "Late")}</span>}
                      {a.earlyLeave && <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-600 font-semibold">{T("早退", "Early out")}</span>}
                      {!a.late && !a.earlyLeave && <span className="text-slate-300">-</span>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </ResponsiveTable>
        </div>
      )}

      {tab === "report" && canView && (
        <>
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="hrms-att-trend">
          <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5 mb-2"><TrendingUp className="h-3.5 w-3.5 text-red-500" />{T("考勤纪律趋势（近 8 周 · 迟到 / 缺下班卡 / 早退 次数）", "Attendance discipline trend (8 weeks · late / missing clock-out / early-out)")}
            {canManage && (
              <select data-testid="hrms-trend-dept" value={trendDept} onChange={(e) => setTrendDept(e.target.value)}
                className="ml-auto text-[11px] rounded-md border border-slate-200 px-2 py-1 font-normal">
                <option value="">{T("全部部门", "All departments")}</option>
                {Object.keys(DEPTS).map((k) => <option key={k} value={k}>{zh ? DEPTS[k] : k}</option>)}
              </select>
            )}
          </p>
          {trend === null ? (
            <div className="h-[180px] flex items-center justify-center text-slate-300"><Loader2 className="h-4 w-4 animate-spin" /></div>
          ) : (
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ top: 4, right: 12, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="late" name={T("迟到", "Late")} stroke="#ef4444" strokeWidth={2} dot={{ r: 2.5 }} />
                  <Line type="monotone" dataKey="noOut" name={T("缺下班卡", "No clock-out")} stroke="#f59e0b" strokeWidth={2} dot={{ r: 2.5 }} />
                  <Line type="monotone" dataKey="early" name={T("早退", "Early out")} stroke="#7B5CFF" strokeWidth={2} dot={{ r: 2.5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <ResponsiveTable className="w-full text-sm">
            <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
              <th className="px-3 py-2">{T("员工", "Employee")}</th><th className="px-3 py-2">{T("出勤天数", "Days")}</th><th className="px-3 py-2">{T("普通考勤工时", "Regular clock hours")}</th>
              <th className="px-3 py-2">OT 1.5×</th><th className="px-3 py-2">OT 2×</th><th className="px-3 py-2">{T("迟到", "Late")}</th><th className="px-3 py-2">{T("早退", "Early out")}</th>
              {/* 加班另列，既能对照批准/实际差异，也保留普通考勤原有口径。 */}
              {[T('批准加班', 'Approved OT'), T('实际加班', 'Actual OT'), T('已确认加班', 'Confirmed OT'), T('待核对加班', 'OT to review'), T('异常记录', 'Exceptions'), T('总记录工时（去重）', 'Recorded hours (deduplicated)')].map(label => <th key={label} className="px-3 py-2 whitespace-nowrap">{label}</th>)}
            </tr></thead>
            <tbody>
              {report === null ? <tr><td colSpan={13} className="text-center py-8 text-slate-300"><Loader2 className="h-4 w-4 animate-spin inline" /></td></tr> :
                report.length === 0 ? <tr><td colSpan={13} className="text-center py-8 text-slate-300">{T("本月暂无数据", "No data this month")}</td></tr> :
                report.map((r) => (
                  <tr key={r.employeeId} className="border-b border-slate-50" data-testid={`hrms-att-report-${r.employeeId}`}>
                    <td className="px-3 py-2 font-semibold text-slate-700">{r.employeeName}</td>
                    <td className="px-3 py-2">{r.days}</td>
                    <td className="px-3 py-2">{r.hours}h</td>
                    <td className="px-3 py-2">{r.ot15 ? <span className="text-amber-600 font-semibold">{r.ot15}h</span> : "-"}</td>
                    <td className="px-3 py-2">{r.ot20 ? <span className="text-red-600 font-semibold">{r.ot20}h</span> : "-"}</td>
                    <td className="px-3 py-2">{r.late || "-"}</td>
                    <td className="px-3 py-2">{r.earlyLeave || "-"}</td>
                    {['approvedOvertimeHours', 'actualOvertimeHours', 'confirmedOvertimeHours', 'pendingOvertimeHours'].map(key => <td key={key} className={`px-3 py-2 ${key === 'pendingOvertimeHours' && r[key] ? 'font-semibold text-amber-700' : ''}`}>{Number(r[key] || 0).toFixed(2)}h</td>)}
                    <td className="px-3 py-2">{r.overtimeExceptions || 0}</td>
                    <td className="px-3 py-2 font-semibold">{Number(r.totalRecordedHours ?? r.hours ?? 0).toFixed(2)}h</td>
                  </tr>
                ))}
            </tbody>
          </ResponsiveTable>
        </div>
        </>
      )}
    </div>
  );
}
